import { AxiosResponse } from "axios";
import { Repository } from "@react3l/react3l/core";
import { kebabCase, url } from "@react3l/react3l/helpers";
import { httpConfig } from "config/http";
import { BASE_API_URL } from "config/consts";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import nameof from "ts-nameof.macro";

import { API_DEVICE_TYPE_PREFIX } from "config/api-consts";
import { DeviceType, DeviceTypeFilter } from "models/DeviceType";
import { Category, CategoryFilter } from "models/Category";

export class DeviceTypeRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = url(BASE_API_URL, API_DEVICE_TYPE_PREFIX);
  }

  public count = (deviceTypeFilter?: DeviceTypeFilter): Observable<number> => {
    return this.httpObservable
      .post<number>(kebabCase(nameof(this.count)), deviceTypeFilter)
      .pipe(map((response: AxiosResponse<number>) => response.data));
  };

  public list = (
    deviceTypeFilter?: DeviceTypeFilter
  ): Observable<DeviceType[]> => {
    return this.httpObservable
      .post<DeviceType[]>(kebabCase(nameof(this.list)), deviceTypeFilter)
      .pipe(map((response: AxiosResponse<DeviceType[]>) => response.data));
  };

  public get = (id: number | string): Observable<DeviceType> => {
    return this.httpObservable
      .post<DeviceType>(kebabCase(nameof(this.get)), { id })
      .pipe(map((response: AxiosResponse<DeviceType>) => response.data));
  };

  public create = (deviceType: DeviceType): Observable<DeviceType> => {
    return this.httpObservable
      .post<DeviceType>(kebabCase(nameof(this.create)), deviceType)
      .pipe(map((response: AxiosResponse<DeviceType>) => response.data));
  };

  public update = (deviceType: DeviceType): Observable<DeviceType> => {
    return this.httpObservable
      .post<DeviceType>(kebabCase(nameof(this.update)), deviceType)
      .pipe(map((response: AxiosResponse<DeviceType>) => response.data));
  };

  public delete = (deviceType: DeviceType): Observable<DeviceType> => {
    return this.httpObservable
      .post<DeviceType>(kebabCase(nameof(this.delete)), deviceType)
      .pipe(map((response: AxiosResponse<DeviceType>) => response.data));
  };

  public save = (
    deviceType: DeviceType,
    isDetail: boolean
  ): Observable<DeviceType> => {
    return isDetail ? this.update(deviceType) : this.create(deviceType);
  };

  public singleListCategory = (
    categoryFilter: CategoryFilter
  ): Observable<Category[]> => {
    return this.httpObservable
      .post<Category[]>(
        kebabCase(nameof(this.singleListCategory)),
        categoryFilter
      )
      .pipe(map((response: AxiosResponse<Category[]>) => response.data));
  };

  public bulkDelete = (idList: number[] | string[]): Observable<void> => {
    return this.httpObservable
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .pipe(map((response: AxiosResponse<void>) => response.data));
  };

  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.httpObservable
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(map((response: AxiosResponse<void>) => response.data));
  };

  public export = (filter: any): Observable<AxiosResponse<any>> => {
    return this.httpObservable.post("export", filter, {
      responseType: "arraybuffer",
    });
  };

  public exportTemplate = (): Observable<AxiosResponse<any>> => {
    return this.httpObservable.post(
      "export-template",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };
}

export const deviceTypeRepository = new DeviceTypeRepository();
